import {
    BlurEvent, CityState, ClickEvent, Component, DataSourceMode, Label, Layout, List,
    LookupModelSearchEvent, LookupModelSelectionEvent, Panel, Tab, TableAddRowResult, TableContentsChangedEvent, TableRow, TableRowBeforeSaveEvent,
    TabStyle, Textbox
} from "@mcleod/components";
import {
    Api, HorizontalAlignment, Model, ModelRow, ModelSearchResult, ServerError, StringUtil, UrlUtil
} from "@mcleod/core";
import { getDispatchControlValue } from "@mcleod/general";
import { AutogenLayoutCarrierCapacity } from "./autogen/AutogenLayoutCarrierCapacity";
import { AvailableLoads } from "./AvailableLoads";
import { InputParser } from "@mcleod/components/src/components/textbox/InputParser";
import { CommonDialogs } from "@mcleod/common";

export class CarrierCapacity extends AutogenLayoutCarrierCapacity {
    private _totalAddedTractors: number;

    get totalAddedTractors() {
        return this._totalAddedTractors;
    }

    set totalAddedTractors(value: number) {
        this._totalAddedTractors = value;
        this.tabAddTractors.caption = this.getTabAddTractorsCaption();
    }

    override async onLoad(): Promise<void> {
        this.styleTab(this.tabAddTractors);
        this.totalAddedTractors = 0;
        this.addLayoutLoadListener(() => {
            this.textboxCarrierName.focus();
        });
        this.tableAddedTractors.doOnRowDelete = (row: TableRow) => {
            const tab = this.tabsetAvailableLoads.findComponentById(`tab${row.data.get("id") }`) as Tab;
            this.tabsetAvailableLoads.remove(tab);
            if (this.tabsetAvailableLoads.components.length < 1) {
                this.panelAvailableLoads.visible = false;
                this.textboxCarrierName.enabled = true;
                this.textboxCarrierContactName.enabled = true;
                this.textboxCallBackNumber.enabled = true;
            }
        };
        this.tabsetAvailableLoads["_header"].backgroundColor = "primary.reverse"; // temp code since we can't set the color via the UI Designer

        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (urlProps && urlProps.carrier) {
            const carrierId = urlProps.carrier;
            await this.getCarrierDetails(carrierId, "id");
            if (this.sourceCarrier.activeRow.isNull("def_loc_radius")) {
                this.sourceCarrier.activeRow.set("def_loc_radius", getDispatchControlValue("def_loc_radius") ? getDispatchControlValue("def_loc_radius") : 60);
            }
            if (this.sourceCarrier.activeRow.isNull("def_load_to_radius")) {
                this.sourceCarrier.activeRow.set("def_load_to_radius", getDispatchControlValue("def_load_to_radius"));
            }
            this.sourceCarrier.activeRow.set("max_loads_per_tractor", getDispatchControlValue("max_loads_per_tractor") ? getDispatchControlValue("max_loads_per_tractor") : 25);

            this.disableCarrierFields();
        }
        if (urlProps && urlProps.managed) {
            const availTractDetailIdList = (urlProps.managed as string).split(",");
            availTractDetailIdList.forEach(detailId =>
                //Use Mode.search instead of datasource to get all field values.
                Model.searchSingleRecord("lme/powerbroker/avail-tract-detail", {id: detailId})
                .then(rowAvailTractDetail => {
                    //These fields do not exist for avail_tract_detail but we augment the row with these values onLoad
                    rowAvailTractDetail.set("carrier_name", this.sourceCarrier.activeRow.get("payee.name"));
                    rowAvailTractDetail.set("cutoff_date", rowAvailTractDetail.getDate("available_date", null))
                    const tableRowResult: TableAddRowResult = this.tableAddedTractors.addRow(rowAvailTractDetail, null, { addToData: true, display: true, save: false });
                    this.addAvailableLoadsTab(tableRowResult.row);
                })
            );

        }
    }

    styleTab(tab: Tab) {
        if (tab.heading != null && tab.headingLabel != null) {
            tab.heading.setProps({ color: "subtle.light", fontBold: false, borderWidth: 0 });
            tab.headingLabel.setProps({ color: "default.light", fontSize: "medium", fontBold: true });
        }
    }

    getTabAddTractorsCaption() {
        return "Manage Added Tractors: " + this.totalAddedTractors;
    }

    buttonAddTractorOnClick(event: ClickEvent) {
        const carrierValid = this.sourceCarrier.validateSimple()
        const tractorValid = this.sourceAvailTractDetail.validateSimple()
        if (carrierValid && tractorValid) {
            const modelRow = new ModelRow(this.sourceAvailTractDetail.url, false, null);
            const locModelRow = this.citystateLocation.textCombined.getFirstLookupModelData() as ModelRow;
            modelRow.set("location_city", locModelRow.get("name"));
            modelRow.set("city_id", locModelRow.get("id"));
            modelRow.set("location_zip", locModelRow.get("zip_code"));
            modelRow.set("location_state", locModelRow.get("state_id"));
            modelRow.set("carrier_id", this.sourceCarrier.activeRow.get("payee.id"));
            modelRow.set("carrier_name", this.sourceCarrier.activeRow.get("payee.name"));
            modelRow.set("carrier_email", this.sourceCarrier.activeRow.get("contact.email"));
            modelRow.set("carrier_phone", this.sourceCarrier.activeRow.get("calculated_phone"));
            modelRow.set("contact", this.sourceCarrier.activeRow.get("contact.name"));

            //Make Carrier fields read only because user should not be able to add multiple tractors for different carriers
            this.disableCarrierFields();

            const loadToModelRow = this.citystateLoadTo.textCombined.getFirstLookupModelData() as ModelRow;
            if (loadToModelRow) {
                modelRow.set("load_to_city_id", loadToModelRow.get("id"));
                modelRow.set("load_to_city", loadToModelRow.get("name"));
                modelRow.set("load_to_state", loadToModelRow.get("state_id"));
            }
            modelRow.set("available_date", new InputParser(this.textboxAvailable).dataValue);
            modelRow.set("cutoff_date", new InputParser(this.textboxAvailable).dataValue);
            modelRow.set("comments", StringUtil.isEmptyString(this.textboxComments.getDataValue()) ? null : this.textboxComments.getDataValue());
            modelRow.setLookupModelData("equipment_type_id", this.textboxTrailerType.getFirstLookupModelData())
            modelRow.set("equipment_type_id", StringUtil.isEmptyString(this.textboxTrailerType.getDataValue()) ? null : this.textboxTrailerType.getDataValue());
            if (this.sourceCarrier.activeRow.isNull("def_loc_radius")) {
                this.sourceCarrier.activeRow.set("def_loc_radius", getDispatchControlValue("def_loc_radius") ? getDispatchControlValue("def_loc_radius") : 60);
            }
            if (this.sourceCarrier.activeRow.isNull("def_load_to_radius")) {
                this.sourceCarrier.activeRow.set("def_load_to_radius", getDispatchControlValue("def_load_to_radius"));
            }

            this.sourceCarrier.activeRow.set("max_loads_per_tractor", getDispatchControlValue("max_loads_per_tractor") ? getDispatchControlValue("max_loads_per_tractor") : 25);
            modelRow.set("distance_radius", this.sourceCarrier.activeRow.get("def_loc_radius"));
            const tableRowResult: TableAddRowResult = this.tableAddedTractors.addRow(modelRow, null, { display: true, addToData: true, save: false });
            this.buttonAddTractor.busy = true;
            tableRowResult.row.data.post().then(() => {
                this.addAvailableLoadsTab(tableRowResult.row)
            }).catch((error: ServerError) => {
               this.tableAddedTractors.deleteRow(tableRowResult.row.index);
               this.totalAddedTractors = this.tableAddedTractors.allRows.length;
               this.citystateLocation.textCombined.clear();
               this.citystateLoadTo.textCombined.clear();
               CommonDialogs.showServerError(error);
            }).finally(() => this.buttonAddTractor.busy = false);
        }
    }

    disableCarrierFields() {
        this.textboxCarrierName.enabled = false;
        this.textboxCarrierContactName.enabled = false;
        this.textboxCallBackNumber.enabled = false;
    }

    tableAddedTractorsBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const row = event.getTableRow();
        const citystateLoadTo = row.findComponentById("citystateLoadToTable") as CityState;
        if (StringUtil.isEmptyString(citystateLoadTo.textCombined.text)) {
            row.data.set("load_to_city_id", null);
            row.data.set("load_to_city", null);
            row.data.set("load_to_state", null);
            citystateLoadTo.textCombined.clear();
        }
        const tab = this.tabsetAvailableLoads.findComponentById(`tab${row.data.get("id") }`) as Tab;
        const availableLoadsLayout = tab.components[0] as AvailableLoads;
        availableLoadsLayout.availableLoadProps = {
            location_city_id: row.data.get("city_id"),
            location_city_name: row.data.get("location_city"),
            location_state: row.data.get("location_state"),
            load_to_city_id: row.data.get("load_to_city_id"),
            load_to_city_name: row.data.get("load_to_city"),
            load_to_state: row.data.get("load_to_state"),
            cutoff_date: row.data.get("available_date"),
            trailer_type: row.data.get("equipment_type_id"),
            location_radius: this.sourceCarrier.activeRow.get("def_loc_radius"),
            load_to_radius: this.sourceCarrier.activeRow.get("def_load_to_radius"),
            max_loads: this.sourceCarrier.activeRow.get("max_loads_per_tractor"),
            carrier_id: this.sourceCarrier.activeRow.get("payee.id")
        };
        availableLoadsLayout.sourceCarrier = this.sourceCarrier;
        availableLoadsLayout.searchForLoads();
    }


    textboxMcNumberOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox = event.target as Textbox;
            const carrierMCNumber = textbox.text;
            this.populateTextboxFromIccNumber(carrierMCNumber);
        }
    }

    getCarrierDetails(value: String, toSearchOn: string): Promise<void> {
        const filter = { [toSearchOn]: value };
        return this.sourceCarrier.search(filter).then((result: ModelSearchResult) => {
            if (result.modelRows?.length == 0) {
                this.labelWarningImage.visible = false;
                this.sourceCarrier.setRowsAndMode(DataSourceMode.NONE, [new ModelRow(this.sourceCarrier.url, false, null)]);
                this.sourceCarrier.activeRow.set("icc_number", value);
            } else {
                this.sourceCarrier.activeRow.setLookupModelData("payee.id", {
                    id: this.sourceCarrier.activeRow.get("payee.id", ""),
                    name: this.sourceCarrier.activeRow.get("payee.name", "")
                })
                this.sourceCarrier.activeRow.setLookupModelData("contact.name", { name: this.sourceCarrier.activeRow.get("contact.name", "") })
                this.sourceCarrier.displayDataInBoundComponents();
                this.validateCarrierForDispatch(this.sourceCarrier.activeRow.get("id"));
            }
        });
    }

    addAvailableLoadsTab(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        this.panelAvailableLoads.visible = true;
        const tab = new Tab({ id: `tab${modelRow.get("id")}` });
        const availableLoads = Layout.getLayout("lme/powerbroker/AvailableLoads") as AvailableLoads;
        availableLoads.capacityLayout = this;
        availableLoads.tabAvailableLoads = tab;
        availableLoads.sourceCarrier = this.sourceCarrier;
        availableLoads.addLayoutLoadListener(() => {
            availableLoads.availableLoadProps = {
                location_city_id: modelRow.get("city_id"),
                location_city_name: modelRow.get("location_city"),
                location_state: modelRow.get("location_state"),
                load_to_city_id: modelRow.get("load_to_city_id"),
                load_to_city_name: modelRow.get("load_to_city"),
                load_to_state: modelRow.get("load_to_state"),
                cutoff_date: modelRow.get("cutoff_date"),
                trailer_type: modelRow.get("equipment_type_id"),
                location_radius: this.sourceCarrier.activeRow.get("def_loc_radius"),
                load_to_radius: this.sourceCarrier.activeRow.get("def_load_to_radius"),
                max_loads: this.sourceCarrier.activeRow.get("max_loads_per_tractor"),
                carrier_id: this.sourceCarrier.activeRow.get("payee.id"),
            };
            availableLoads.carrierAssignmentProps = {
                payee_id: this.sourceCarrier.activeRow.get("id"),
                carrier_name: this.sourceCarrier.activeRow.get("payee.name"),
                icc_number: this.sourceCarrier.activeRow.get("icc_number"),
                dot_number: this.sourceCarrier.activeRow.get("dot_number"),
                carrier_contact: this.sourceCarrier.activeRow.get("contact.name"),
                carrier_phone: this.sourceCarrier.activeRow.get("calculated_phone"),
                carrier_email: this.sourceCarrier.activeRow.get("contact.email"),
                broker_avail_tract: modelRow.get("tractor_id"),
            }
            availableLoads.searchForLoads();
        });
        tab.add(availableLoads);
        tab.tabStyle = TabStyle.OUTLINED;
        this.tabsetAvailableLoads.add(tab)
        this.createTabHeading(tab, modelRow);
        //must add closeable last or the x will be on top of the panels, a little hacky
        tab.closeable = true;
        tab.addAfterCloseListener(() => {
            this.tableAddedTractors.deleteRow(tableRow.index);
            this.totalAddedTractors = this.tableAddedTractors.allRows.length;
            if (this.tableAddedTractors.allRows.length < 1) {
                this.panelAvailableLoads.visible = false;
                this.panelAvailableLoads.visible = false;
                this.textboxCarrierName.enabled = true;
                this.textboxCarrierContactName.enabled = true;
                this.textboxCallBackNumber.enabled = true;
                this.textboxEmailAddress.enabled = true;
            }
        })
        this.tabsetAvailableLoads.selectedIndex = tab._index;
        this.citystateLocation.textCombined.clear();
        this.citystateLoadTo.textCombined.clear();
        this.textboxComments.clear();
        this.textboxTrailerType.clear();
    }

    createTabHeading(tab: Tab, modelRow: ModelRow) {
        // Location and Destination Portion
        const originString = modelRow.get("location_city") + ", " + " " + modelRow.get("location_state");
        let destinationString: string;
        if (modelRow.isNull("load_to_city")) {
            destinationString = "ANYWHERE";
        } else {
            destinationString = modelRow.get("load_to_city") + ", " + " " + modelRow.get("load_to_state");
        }
        const panelStops = new Panel({ rowBreak: false, marginRight: 10 });
        const labelOrigin = new Label({
            fontSize: "small",
            text: originString,
            rowBreak: false,
            paddingBottom: 0,
            id: "labelOrigin"
        });
        const labelTo = new Label({
            fontSize: "xsmall",
            text: "to",
            padding: 0,
            rowBreak: true,
            color: "default.lightest"
        });
        const labelDestination = new Label({
            fontSize: "small",
            text: destinationString,
            rowBreak: false,
            id: "labelDestination"
        });
        panelStops.addFromArray([labelOrigin, labelTo, labelDestination]);

        // Total Loads Portion
        const panelTotal = new Panel({ rowBreak: false, id: "panelTotal" });
        const labelTotalTop = new Label({
            fontSize: "xsmall",
            text: "Total",
            rowBreak: true,
            fillRow: true,
            align: HorizontalAlignment.CENTER,
            paddingBottom: 0
        });
        const labelTotalVal = new Label({
            fontSize: "medium",
            text: "0",
            id: "labelTotalVal",
            rowBreak: false,
            fillRow: true,
            align: HorizontalAlignment.CENTER
        });
        panelTotal.addFromArray([labelTotalTop, labelTotalVal]);

        tab.heading.addFromArray([panelStops, panelTotal]);
    }

    textboxTrailerTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.payee_id = this.sourceCarrier.activeRow?.get("id");
        event.filter.applies_to = "L";
    }

    async populateTextboxFromIccNumber(mcNumber: String) {
        if (mcNumber != null && mcNumber.length > 0) {
            this.getCarrierDetails(mcNumber, "icc_number");
        }
    }

    textboxCarrierNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selectedCarrier = event.selection;
            this.getCarrierDetails(selectedCarrier.get("id"), "id");
            this.validateCarrierForDispatch(selectedCarrier.get("id", null));
        } else if (event.hasDeselection()) {
            this.sourceCarrier.clear();
            this.labelWarningImage.visible = false;
        }
        this.sourceCarrier.displayDataInBoundComponents();
    }

    textboxCarrierContactNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const contactRow = event.selection;
            const calculated_phone = !contactRow.isNull("phone") ? contactRow.get("phone") : this.sourceCarrier.activeRow.get("payee.phone_number");
            this.sourceCarrier.activeRow.set("calculated_phone", calculated_phone);
            this.sourceCarrier.activeRow.set("contact.email", contactRow.get("email"));
        } else if (event.hasDeselection()) {
            this.sourceCarrier.activeRow.setValues({
                "contact.name": null,
                "contact.email": null,
                "calculated_phone": null
            });
        }
        this.sourceCarrier.displayDataInBoundComponents();
    }

    validateCarrierForDispatch(payee_id: string) {
        this.labelWarningImage.visible = false;
        Api.search("lme/powerbroker/carrier-qualifications", { "payee_id": payee_id }).then(result => {
            const carrierValidationData = result?.data[0];
            const errorList = carrierValidationData?.failed_items as List;
            if (errorList != null) {
                this.labelWarningImage.visible = true;
                this.labelWarningImage.tooltipDynamicContent = async (): Promise<Layout> => {
                    return await this.getCarrierValidationQuickInfoLayout(payee_id, errorList);
                }
            }
        });
    }

    private async getCarrierValidationQuickInfoLayout(carrierId: string, errorList?: List): Promise<Layout> {
        if (carrierId != null) {
            const layout = await Layout.getLoadedLayout("lme/powerbroker/CarrierValidationWarningQuickInfo");
            const modelRow = new ModelRow(layout.mainDataSource.url, false, { id: carrierId });
            layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [modelRow])
            const items = [];
            if (errorList != null) {
                if (errorList) {
                    for (const error of Object.values(errorList)) {
                        const label = new Label({ text: error });
                        items.push(label);
                    }
                }
            }
            layout["errorList"].items = items;
            return layout;
        }
        return null;
    }

    tableAddedTractorsOnContentsChanged(event: TableContentsChangedEvent) {
        this.totalAddedTractors = this.tableAddedTractors.allRows.length;
    }

    textboxCarrierContactNameBeforeLookupModelSearch(event) {
        if (this.sourceCarrier.activeRow?.get("payee.id")) {
            event.filter.parent_row_type = "P";
            event.filter.parent_row_id = this.sourceCarrier.activeRow.get("payee.id");
            event.filter.is_active = "Y";
        } else {
            event.preventDefault();
        }
    }

    /** This is an event handler for the onBlur event of textboxEmailAddress. */
    textboxEmailAddressOnBlur(event: BlurEvent) {
        this.tabsetAvailableLoads.findComponentsByType(Tab).forEach(tab => {
            const availableLoads = tab.findComponentByType(AvailableLoads) as AvailableLoads;
            availableLoads.carrierAssignmentProps.carrier_email = this.textboxEmailAddress.validationWarning ? "" : this.sourceCarrier.activeRow.get("contact.email");
            availableLoads.configureSelectColumn();
            availableLoads.searchForLoads();
        });
    }
}
